import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import axios from 'axios';
import FieldConfigModal from './FieldConfigModal';
import './SearchDragDropFields.css';
import SortableFieldItem from './SortableFieldItem';

/**
 * Search Drag and Drop Fields Component
 *
 * Provides an interactive interface for managing dynamic search filter fields
 * with drag-and-drop reordering, enable/disable toggles, inline editing,
 * and configuration options for each field type.
 *
 * @since 2.1.0
 * @param {Object} props Component properties
 * @param {Function} props.showToast Toast notification function
 * @param {Function} props.onFieldsChange Callback when fields are modified
 * @param {Array} props.initialFields Initial field configuration array
 */
const SearchDragDropFields = ({ showToast, onFieldsChange, initialFields = [] }) => {
  // State management
  const [searchFields, setSearchFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editType, setEditType] = useState('text');

  // Config modal state
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configField, setConfigField] = useState(null);

  // Ref to prevent race conditions during save operations
  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef(null);

  // API configuration
  const API_BASE = window.HavenlyticsData?.hvnlyNabApi?.url || '/wp-json/hvnlynab/v1';
  const API_NONCE = window.HavenlyticsData?.hvnlyNabApi?.nonce || '';
  const SETTINGS_ENDPOINT = `${API_BASE}/plugin-settings/search`;

  const AXIOS_CONFIG = {
    headers: {
      'X-WP-Nonce': API_NONCE,
      'Content-Type': 'application/json'
    }
  };

  // Configure drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Minimum drag distance before activation
      },
    }),
    useSensor(KeyboardSensor)
  );

  /**
   * Default search fields configuration
   * Provides sensible defaults for all field types with their respective
   * configuration options and settings.
   */
  const DEFAULT_SEARCH_FIELDS = [
    {
      id: 'price',
      title: __('Price', 'havenlytics'),
      type: 'range',
      enabled: true,
      order: 1,
      is_default: true,
      config: {
        minPlaceholder: __('Min Price', 'havenlytics'),
        maxPlaceholder: __('Max Price', 'havenlytics'),
        minOptions: ['100000', '200000', '300000', '400000', '500000', '600000', '700000', '800000', '900000', '1000000'],
        maxOptions: ['200000', '300000', '400000', '500000', '600000', '700000', '800000', '900000', '1000000', '1500000'],
        usePresetValues: true
      }
    },
    {
      id: 'status',
      title: __('Status', 'havenlytics'),
      type: 'dropdown',
      enabled: true,
      order: 2,
      is_default: true,
      config: {
        useTaxonomy: true,
        taxonomy: 'hvnly_prop_depts',
        placeholder: __('Select Status', 'havenlytics')
      }
    },
    {
      id: 'bedrooms_bathrooms',
      title: __('Bedrooms & Bathrooms', 'havenlytics'),
      type: 'group',
      enabled: true,
      order: 3,
      is_default: true,
      config: {
        subFields: [
          { id: 'bedrooms', label: __('Bedrooms', 'havenlytics'), type: 'number', options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'] },
          { id: 'bathrooms', label: __('Bathrooms', 'havenlytics'), type: 'number', options: ['0', '1', '2', '3', '4', '5', '6', '7', '8'] }
        ]
      }
    },
    {
      id: 'reception_rooms',
      title: __('Reception Rooms', 'havenlytics'),
      type: 'number',
      enabled: true,
      order: 4,
      is_default: true,
      config: {
        min: 0,
        max: 10,
        placeholder: __('Any', 'havenlytics'),
        options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
      }
    },
    {
      id: 'property_types',
      title: __('Property Types', 'havenlytics'),
      type: 'checkbox',
      enabled: true,
      order: 5,
      is_default: true,
      config: {
        useTaxonomy: true,
        taxonomy: 'hvnly_prop_types',
        selectAllOption: true
      }
    },
    {
      id: 'locations',
      title: __('Locations', 'havenlytics'),
      type: 'dropdown',
      enabled: true,
      order: 6,
      is_default: true,
      config: {
        useTaxonomy: true,
        taxonomy: 'hvnly_prop_locations',
        placeholder: __('Select Location', 'havenlytics')
      }
    },
    {
      id: 'features',
      title: __('Features', 'havenlytics'),
      type: 'checkbox',
      enabled: true,
      order: 7,
      is_default: true,
      config: {
        useTaxonomy: true,
        taxonomy: 'hvnly_prop_features',
        selectAllOption: true
      }
    },
    {
      id: 'tags',
      title: __('Tags', 'havenlytics'),
      type: 'dropdown',
      enabled: true,
      order: 8,
      is_default: true,
      config: {
        useTaxonomy: true,
        taxonomy: 'hvnly_prop_tags',
        placeholder: __('Select Tags', 'havenlytics')
      }
    },
    {
      id: 'badges',
      title: __('Badges', 'havenlytics'),
      type: 'checkbox',
      enabled: true,
      order: 9,
      is_default: true,
      config: {
        useTaxonomy: true,
        taxonomy: 'hvnly_prop_badges',
        selectAllOption: true
      }
    },
    {
      id: 'property_id',
      title: __('Property ID', 'havenlytics'),
      type: 'text',
      enabled: true,
      order: 10,
      is_default: true,
      config: {
        isLocked: true,
        cannotDelete: true,
        cannotEdit: true
      }
    }
  ];

  /**
   * Initialize component with initial fields or load from API
   */
  useEffect(() => {
    if (initialFields && initialFields.length > 0) {
      const sortedFields = [...initialFields].sort((a, b) => (a.order || 0) - (b.order || 0));
      setSearchFields(sortedFields);
      setIsLoading(false);
    } else {
      loadSearchFields();
    }
  }, [initialFields]);

  /**
   * Load search fields from REST API
   * Fetches saved field configuration or falls back to defaults
   */
  const loadSearchFields = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(SETTINGS_ENDPOINT, AXIOS_CONFIG);

      if (response.data?.success && response.data?.data) {
        const searchData = response.data.data;
        let fields = searchData.hvnly_search_fields || searchData.search_fields || [];

        if (fields.length > 0) {
          fields.sort((a, b) => (a.order || 0) - (b.order || 0));
          setSearchFields(fields);
          if (onFieldsChange) {
            onFieldsChange(fields);
          }
        } else {
          // If no fields, use defaults
          setSearchFields([...DEFAULT_SEARCH_FIELDS]);
          if (onFieldsChange) {
            onFieldsChange([...DEFAULT_SEARCH_FIELDS]);
          }
        }
      } else {
        // If API fails, use defaults
        setSearchFields([...DEFAULT_SEARCH_FIELDS]);
        if (onFieldsChange) {
          onFieldsChange([...DEFAULT_SEARCH_FIELDS]);
        }
      }
    } catch (error) {
      // On error, use defaults
      setSearchFields([...DEFAULT_SEARCH_FIELDS]);
      if (onFieldsChange) {
        onFieldsChange([...DEFAULT_SEARCH_FIELDS]);
      }
      if (showToast) {
        showToast('warning', __('Notice', 'havenlytics'), __('Using default search fields', 'havenlytics'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Save search fields to REST API with race condition prevention
   * Uses refs to queue pending saves while an active save is in progress
   *
   * @param {Array} fields Field configuration to save
   * @returns {Promise<boolean>} Save success status
   */
  const saveSearchFields = async (fields) => {
    if (isSavingRef.current) {
      pendingSaveRef.current = fields;
      return;
    }

    isSavingRef.current = true;

    try {
      const response = await axios.get(SETTINGS_ENDPOINT, AXIOS_CONFIG);
      const currentSettings = response.data?.data || {};

      const updatedSettings = { ...currentSettings };
      updatedSettings.hvnly_search_fields = fields;

      if (updatedSettings.search_fields) {
        delete updatedSettings.search_fields;
      }

      const saveResponse = await axios.post(SETTINGS_ENDPOINT, updatedSettings, AXIOS_CONFIG);

      if (saveResponse.data?.success) {
        if (onFieldsChange) {
          onFieldsChange(fields);
        }

        if (pendingSaveRef.current) {
          const pending = pendingSaveRef.current;
          pendingSaveRef.current = null;
          setTimeout(() => saveSearchFields(pending), 100);
        }

        return true;
      }
      return false;
    } catch (error) {
      if (showToast) {
        showToast('error', __('Error', 'havenlytics'), __('Failed to save search fields', 'havenlytics'));
      }
      return false;
    } finally {
      isSavingRef.current = false;
    }
  };

  /**
   * Move field up in order
   *
   * @param {string} id Field ID to move
   */
  const moveFieldUp = (id) => {
    setSearchFields(prev => {
      const index = prev.findIndex(field => field.id === id);
      if (index === 0) return prev;

      const newFields = [...prev];
      [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];

      // Update order numbers
      newFields.forEach((field, idx) => {
        field.order = idx + 1;
      });

      setTimeout(() => saveSearchFields(newFields), 0);
      return newFields;
    });
  };

  /**
   * Move field down in order
   *
   * @param {string} id Field ID to move
   */
  const moveFieldDown = (id) => {
    setSearchFields(prev => {
      const index = prev.findIndex(field => field.id === id);
      if (index === prev.length - 1) return prev;

      const newFields = [...prev];
      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];

      // Update order numbers
      newFields.forEach((field, idx) => {
        field.order = idx + 1;
      });

      setTimeout(() => saveSearchFields(newFields), 0);
      return newFields;
    });
  };

  /**
   * Toggle field enabled/disabled status
   *
   * @param {string} id Field ID to toggle
   */
  const toggleField = (id) => {
    setSearchFields(prev => {
      const newFields = prev.map(field =>
        field.id === id ? { ...field, enabled: !field.enabled } : field
      );

      setTimeout(() => saveSearchFields(newFields), 0);
      return newFields;
    });
  };

  /**
   * Start inline editing for a field
   *
   * @param {Object} field Field to edit
   */
  const startEditing = (field) => {
    setEditingField(field.id);
    setEditTitle(field.title);
    setEditType(field.type);
  };

  /**
   * Save inline edit changes
   */
  const saveEdit = () => {
    if (!editTitle.trim()) {
      if (showToast) {
        showToast('error', __('Error', 'havenlytics'), __('Field title cannot be empty', 'havenlytics'));
      }
      return;
    }

    setSearchFields(prev => {
      const newFields = prev.map(field =>
        field.id === editingField
          ? { ...field, title: editTitle, type: editType }
          : field
      );

      setTimeout(() => saveSearchFields(newFields), 0);
      return newFields;
    });

    setEditingField(null);
    if (showToast) {
      showToast('success', __('Success', 'havenlytics'), __('Field title updated!', 'havenlytics'));
    }
  };

  /**
   * Cancel inline editing
   */
  const cancelEdit = () => {
    setEditingField(null);
  };

  /**
   * Open configuration modal for a field
   *
   * @param {Object} field Field to configure
   */
  const handleConfigureField = (field) => {
    setConfigField(field);
    setConfigModalOpen(true);
  };

  /**
   * Save field configuration changes
   *
   * @param {Object} updatedField Updated field configuration
   */
  const handleSaveFieldConfig = async (updatedField) => {
    setSearchFields(prev => {
      const newFields = prev.map(field =>
        field.id === updatedField.id ? updatedField : field
      );
      setTimeout(() => saveSearchFields(newFields), 0);
      return newFields;
    });
    showToast('success', __('Success', 'havenlytics'), __('Field configuration saved!', 'havenlytics'));
  };

  /**
   * Handle drag-and-drop reordering
   *
   * @param {Object} event Drag end event
   */
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSearchFields((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);

        const newItems = [...items];
        const [movedItem] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, movedItem);

        // Update order numbers
        newItems.forEach((item, index) => {
          item.order = index + 1;
        });

        setTimeout(() => saveSearchFields(newItems), 0);
        return newItems;
      });
    }
  };

  /**
   * Get human-readable label for field type
   *
   * @param {string} type Field type identifier
   * @returns {string} Display label
   */
  const getTypeLabel = (type) => {
    const types = {
      'text': __('Text', 'havenlytics'),
      'number': __('Number', 'havenlytics'),
      'dropdown': __('Dropdown', 'havenlytics'),
      'checkbox': __('Checkbox', 'havenlytics'),
      'range': __('Range', 'havenlytics'),
      'group': __('Group', 'havenlytics'),
      'date': __('Date', 'havenlytics')
    };
    return types[type] || type;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="hvnly_settings_api_search_drag_drop_builder">
        <div className="hvnly_settings_api_search_drag_drop_loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>{__('Loading search fields...', 'havenlytics')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hvnly_settings_api_search_drag_drop_builder">
      {/* Header Section */}
      <div className="hvnly_settings_api_search_drag_drop_header">
        <i className="fas fa-list"></i>
        <span>{__('Manage Search Filter Fields', 'havenlytics')}</span>
        <span className="hvnly_settings_api_search_drag_drop_hint">
          {__('Drag to reorder | Click Edit to rename | Click Configure to set options', 'havenlytics')}
        </span>
      </div>

      {/* Main Content Area with Drag-and-Drop */}
      <div className="hvnly_settings_api_search_drag_drop_body">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={searchFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
            <div className="hvnly_settings_api_search_drag_drop_list">
              {searchFields.length > 0 ? (
                searchFields.map((field, index) => (
                  <SortableFieldItem
                    key={field.id}
                    field={{ ...field, typeLabel: getTypeLabel(field.type) }}
                    index={index}
                    onMoveUp={moveFieldUp}
                    onMoveDown={moveFieldDown}
                    onToggle={toggleField}
                    onEdit={startEditing}
                    onConfigure={handleConfigureField}
                    isEditing={editingField === field.id}
                    editTitle={editTitle}
                    editType={editType}
                    onEditTitleChange={setEditTitle}
                    onEditTypeChange={setEditType}
                    onSaveEdit={saveEdit}
                    onCancelEdit={cancelEdit}
                    fieldsLength={searchFields.length}
                    isDefault={field.is_default}
                  />
                ))
              ) : (
                <p style={{ textAlign: 'center', padding: '40px' }}>
                  {__('No search fields found.', 'havenlytics')}
                </p>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Field Configuration Modal */}
      <FieldConfigModal
        isOpen={configModalOpen}
        onClose={() => {
          setConfigModalOpen(false);
          setConfigField(null);
        }}
        field={configField}
        onSave={handleSaveFieldConfig}
        showToast={showToast}
      />
    </div>
  );
};

export default SearchDragDropFields;